import React from "react";

const QuizResult = ({ selectedTest, handleButtonClick, deletethis }) => {
    const quizData = JSON.parse(selectedTest.quiz_data);
    const correctCount   = quizData.filter(q => q.correct).length;
    const incorrectCount = quizData.length - correctCount;
    const total          = quizData.length;
    const pct            = total > 0 ? Math.round((correctCount / total) * 100) : 0;

    const grade     = pct >= 90 ? 'A' : pct >= 80 ? 'B' : pct >= 70 ? 'C' : pct >= 60 ? 'D' : 'F';
    const perfLabel = pct >= 90 ? 'Excellent' : pct >= 80 ? 'Above Average' : pct >= 70 ? 'Average' : pct >= 60 ? 'Below Average' : 'Needs Improvement';

    const engItems   = quizData.filter(q => q.tag === 'eng'  || q.tag === 'Eng'  || q.section === 'eng');
    const mathItems  = quizData.filter(q => q.tag === 'math' || q.tag === 'Math' || q.section === 'math');
    const engCorrect  = engItems.filter(q => q.correct).length;
    const mathCorrect = mathItems.filter(q => q.correct).length;

    const topicMap = {};
    quizData.forEach(q => {
        if (!q.correct && q.topic) topicMap[q.topic] = (topicMap[q.topic] || 0) + 1;
    });
    const weakTopics = Object.entries(topicMap).sort((a, b) => b[1] - a[1]).slice(0, 4);

    return (
        <div className="space-y-3">

            {/* ── Performance header ─────────────────────────────────── */}
            <div className="flex items-center justify-between border border-slate-200 bg-slate-50 px-4 py-3">
                <div>
                    <p className="text-[10px] font-medium text-slate-400 uppercase tracking-widest mb-0.5">Overall Performance</p>
                    <p className="text-sm font-semibold text-slate-700">{perfLabel}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{selectedTest.date?.split('T')[0]}</p>
                </div>
                <div className="w-14 h-14 border border-slate-200 bg-white flex items-center justify-center">
                    <span className="text-2xl font-black text-slate-700">{grade}</span>
                </div>
            </div>

            {/* ── Score + stats row ──────────────────────────────────── */}
            <div className="border border-slate-200 p-4">
                <div className="flex items-center gap-3 mb-3">
                    <span className="text-3xl font-bold tabular-nums text-slate-800">{pct}%</span>
                    <div className="flex-1 h-2 bg-slate-100 overflow-hidden">
                        <div
                            className="h-full bg-slate-400 transition-all duration-700"
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-3 divide-x divide-slate-100">
                    <div className="text-center pr-3">
                        <p className="text-xl font-bold text-slate-800">{correctCount}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5 flex items-center justify-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400 inline-block" />
                            Correct
                        </p>
                    </div>
                    <div className="text-center px-3">
                        <p className="text-xl font-bold text-slate-800">{incorrectCount}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5 flex items-center justify-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300 inline-block" />
                            Incorrect
                        </p>
                    </div>
                    <div className="text-center pl-3">
                        <p className="text-xl font-bold text-slate-500">{total}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Total</p>
                    </div>
                </div>
            </div>

            {/* ── Section Breakdown ──────────────────────────────────── */}
            {engItems.length > 0 && mathItems.length > 0 && (
                <div className="border border-slate-200 p-4">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3">Section Breakdown</p>
                    <div className="space-y-2.5">
                        {[
                            { label: 'Reading & Writing', correct: engCorrect, total: engItems.length },
                            { label: 'Math',              correct: mathCorrect, total: mathItems.length },
                        ].map(({ label, correct, total: t }) => {
                            const p = t > 0 ? Math.round((correct / t) * 100) : 0;
                            return (
                                <div key={label}>
                                    <div className="flex justify-between mb-1">
                                        <span className="text-xs text-slate-600">{label}</span>
                                        <span className="text-xs text-slate-500 tabular-nums">{correct}/{t} · {p}%</span>
                                    </div>
                                    <div className="h-1.5 bg-slate-100 overflow-hidden">
                                        <div className="h-full bg-slate-400 transition-all duration-700" style={{ width: `${p}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Weak Topics ────────────────────────────────────────── */}
            {weakTopics.length > 0 && (
                <div className="border border-slate-200 p-4">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3">Topics to Review</p>
                    <div className="space-y-1.5">
                        {weakTopics.map(([topic, count]) => (
                            <div key={topic} className="flex items-center gap-2.5 py-1">
                                <span className="w-1 h-1 rounded-full bg-slate-400 shrink-0" />
                                <span className="text-xs text-slate-600 flex-1 leading-tight">{topic}</span>
                                <span className="text-[10px] text-slate-400 tabular-nums shrink-0">{count}×</span>
                            </div>
                        ))}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-3 leading-relaxed border-t border-slate-100 pt-2">
                        Focus your next session on these topics to improve your score.
                    </p>
                </div>
            )}

            {/* ── Question Grid ──────────────────────────────────────── */}
            <div className="border border-slate-200 p-4">
                <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Question Overview</p>
                    <div className="flex items-center gap-3 text-[10px] text-slate-400">
                        <span className="flex items-center gap-1.5">
                            <span className="w-2 h-0.5 bg-slate-500 inline-block" /> Correct
                        </span>
                        <span className="flex items-center gap-1.5">
                            <span className="w-2 h-0.5 bg-slate-300 inline-block" /> Incorrect
                        </span>
                    </div>
                </div>
                <div className="max-h-[280px] overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 transparent' }}>
                    <div className="grid grid-cols-6 sm:grid-cols-7 md:grid-cols-8 gap-1.5 pb-1">
                        {quizData.map((question, index) => (
                            <div key={index} className="flex flex-col items-center gap-1 group">
                                <button
                                    onClick={() => {
                                        if (selectedTest.type === "ACT") {
                                            handleButtonClick(JSON.parse(selectedTest.quiz_data)[index].selectedOption, JSON.parse(selectedTest.quiz_data)[index].qid, question.section, selectedTest.type, JSON.parse(selectedTest.quiz_data), index);
                                        } else if (selectedTest.type === "AP_Calc_AB" || selectedTest.type === "AP_Calc_BC") {
                                            handleButtonClick(question.selectedOption, question.question_id, question.tag, selectedTest.type, JSON.parse(selectedTest.quiz_data), index, question.section);
                                        } else {
                                            handleButtonClick(JSON.parse(selectedTest.quiz_data)[index].selectedOption, JSON.parse(selectedTest.quiz_data)[index].questionIds, question.tag || question.section, selectedTest.type, JSON.parse(selectedTest.quiz_data), index);
                                        }
                                    }}
                                    className={`w-9 h-9 flex items-center justify-center text-xs font-medium transition-all duration-150 border-t-2 ${
                                        question.correct
                                            ? "bg-white border-t-slate-500 text-slate-700 border border-slate-200 hover:bg-slate-50"
                                            : "bg-slate-50 border-t-slate-300 text-slate-500 border border-slate-200 hover:bg-slate-100"
                                    }`}
                                >
                                    {index + 1}
                                </button>
                                <span className="text-[8px] text-slate-400 group-hover:text-slate-600 transition-colors text-center leading-tight w-full truncate px-0.5">
                                    {question.topic}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Actions ────────────────────────────────────────────── */}
            <div className="flex gap-2 pb-2">
                <button
                    className="flex-1 py-2.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors"
                    onClick={() => window.open(`/dashboard/results/sat/quiz/${selectedTest.test_id}`, '_blank')}
                >
                    Full Report
                </button>
                <button
                    className="py-2.5 px-4 text-xs font-medium text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors"
                    onClick={() => deletethis()}
                >
                    Delete
                </button>
            </div>

        </div>
    );
};

export default QuizResult;
